<template>
  <div class="outreach-cue-cards" :style="sectionStyle">
    <div v-if="hasNoICPs && !props.icpLoading" class="outreach-cue-cards__empty-state">
      <div class="outreach-cue-cards__empty-content">
        <Icon icon="mdi:sparkles" class="outreach-cue-cards__empty-icon" />
        <span class="outreach-cue-cards__empty-text">Set up ICP for AI personalization</span>
      </div>
      <Button
        variant="primary"
        size="sm"
        icon="mdi:arrow-right"
        icon-position="right"
        class="outreach-cue-cards__empty-btn"
        @click="goToICPSettings"
      >
        Set up ICP
      </Button>
    </div>

    <template v-else>
      <div
        v-if="(state === 'initial' && props.icpLoading) || state === 'checking'"
        class="outreach-cue-cards__trigger-wrapper"
      >
        <button
          type="button"
          class="outreach-cue-cards__trigger outreach-cue-cards__trigger--loading"
          disabled
        >
          <Icon icon="mdi:loading" class="outreach-cue-cards__spinner" />
          <span>Loading...</span>
        </button>
      </div>

      <div
        v-else-if="state === 'initial'"
        class="outreach-cue-cards__trigger-wrapper"
        ref="dropdownRef"
      >
        <button
          v-tooltip="{ content: USAGE_BLOCKED_TOOLTIP, disabled: !isUsageBlocked }"
          type="button"
          class="outreach-cue-cards__trigger outreach-cue-cards__trigger--initial"
          :disabled="isUsageBlocked"
          @click="toggleDropdown"
        >
          <Icon icon="mdi:sparkles" class="outreach-cue-cards__trigger-icon" />
          <span>Generate All Outreach Assets</span>
          <Icon
            icon="mdi:chevron-down"
            class="outreach-cue-cards__trigger-chevron"
            :class="{ 'outreach-cue-cards__trigger-chevron--open': isDropdownOpen }"
          />
        </button>

        <div v-if="isDropdownOpen" class="outreach-cue-cards__dropdown">
          <div class="outreach-cue-cards__dropdown-header">Select ICP</div>
          <div class="outreach-cue-cards__dropdown-list">
            <button
              v-for="option in selectableOptions"
              :key="option.value"
              type="button"
              class="outreach-cue-cards__dropdown-option"
              @click="handleICPSelect(option.value)"
            >
              <span class="outreach-cue-cards__dropdown-option-label">{{ option.label }}</span>
              <span
                class="outreach-cue-cards__dropdown-option-edit"
                @click.stop="goToEditICP(option.value)"
                title="Edit ICP"
              >
                <Icon icon="mdi:pencil-outline" />
              </span>
            </button>
          </div>
          <div class="outreach-cue-cards__dropdown-divider" />
          <button type="button" class="outreach-cue-cards__dropdown-add" @click="goToAddICP">
            <Icon icon="mdi:plus" class="outreach-cue-cards__dropdown-add-icon" />
            <span>Add New ICP</span>
          </button>
        </div>
      </div>

      <div v-else-if="state === 'loading'" class="outreach-cue-cards__trigger-wrapper">
        <button
          type="button"
          class="outreach-cue-cards__trigger outreach-cue-cards__trigger--loading"
          disabled
        >
          <Icon icon="mdi:loading" class="outreach-cue-cards__spinner" />
          <span>Generating...</span>
        </button>
      </div>

      <div v-else-if="state === 'error'" class="outreach-cue-cards__trigger-wrapper">
        <div class="outreach-cue-cards__error">
          <div class="outreach-cue-cards__error-content">
            <Icon icon="mdi:alert-circle-outline" class="outreach-cue-cards__error-icon" />
            <span class="outreach-cue-cards__error-text">
              Generation timed out. Results may still be processing.
            </span>
          </div>
          <div class="outreach-cue-cards__error-actions">
            <button
              type="button"
              class="outreach-cue-cards__error-btn outreach-cue-cards__error-btn--retry"
              @click="retryFromHistory"
            >
              <Icon icon="mdi:arrow-clockwise" />
              <span>Check for Results</span>
            </button>
            <button
              v-tooltip="{ content: USAGE_BLOCKED_TOOLTIP, disabled: !isUsageBlocked }"
              type="button"
              class="outreach-cue-cards__error-btn outreach-cue-cards__error-btn--regen"
              :disabled="isUsageBlocked"
              @click="handleRegenerate"
            >
              <Icon icon="mdi:refresh" />
              <span>Regenerate</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="state === 'ready'" class="outreach-cue-cards__ready-wrapper" ref="readyRef">
        <button
          ref="readyTriggerRef"
          type="button"
          class="outreach-cue-cards__trigger outreach-cue-cards__trigger--ready"
          @click="togglePanel"
        >
          <Icon icon="mdi:check-circle" class="outreach-cue-cards__trigger-icon" />
          <span>Outreach Cue Cards</span>
          <Icon
            icon="mdi:chevron-down"
            class="outreach-cue-cards__trigger-chevron"
            :class="{ 'outreach-cue-cards__trigger-chevron--open': isPanelOpen }"
          />
        </button>

        <div v-if="isPanelOpen" class="outreach-cue-cards__panel" :style="panelPositionStyle">
          <div class="outreach-cue-cards__toolbar">
            <button type="button" class="outreach-cue-cards__toolbar-btn" @click="handleSave">
              <Icon :icon="isSaved ? 'mdi:content-save' : 'mdi:content-save-outline'" />
              <span>{{ isSaved ? "Saved" : "Save" }}</span>
            </button>
            <button type="button" class="outreach-cue-cards__toolbar-btn" @click="handleHistory">
              <Icon icon="mdi:history" />
              <span>History</span>
            </button>
            <button
              v-tooltip="{ content: USAGE_BLOCKED_TOOLTIP, disabled: !isUsageBlocked }"
              type="button"
              class="outreach-cue-cards__toolbar-btn"
              :disabled="isUsageBlocked"
              @click="handleRegenerate"
            >
              <Icon icon="mdi:refresh" />
              <span>Regenerate</span>
            </button>
            <button
              type="button"
              class="outreach-cue-cards__toolbar-btn outreach-cue-cards__toolbar-btn--reset"
              @click="handleReset"
              title="Change ICP"
            >
              <Icon icon="mdi:restart" />
            </button>
          </div>

          <div v-if="isHistoryOpen" class="outreach-cue-cards__history">
            <div v-if="isHistoryLoading" class="outreach-cue-cards__history-loading">
              <Icon icon="mdi:loading" class="outreach-cue-cards__spinner" />
              <span>Loading history...</span>
            </div>
            <div v-else-if="historyItems.length === 0" class="outreach-cue-cards__history-empty">
              <span>No past generations found</span>
            </div>
            <div v-else class="outreach-cue-cards__history-list">
              <button
                v-for="item in historyItems"
                :key="item.id"
                type="button"
                class="outreach-cue-cards__history-item"
                :class="{ 'outreach-cue-cards__history-item--active': item.id === currentRecordId }"
                @click="loadHistoryItem(item)"
              >
                <div class="outreach-cue-cards__history-item-left">
                  <span class="outreach-cue-cards__history-item-time">{{
                    formatRelativeTime(item.createdAt)
                  }}</span>
                  <span class="outreach-cue-cards__history-item-preview">{{
                    getHistoryPreview(item)
                  }}</span>
                </div>
                <Icon
                  v-if="item.isSaved"
                  icon="mdi:content-save"
                  class="outreach-cue-cards__history-item-saved"
                />
              </button>
            </div>
          </div>

          <div class="outreach-cue-cards__list">
            <OutreachCueCard
              v-for="card in cueCardConfigs"
              :key="card.channel"
              :config="card"
              :data="cueCardsData!"
              :is-expanded="expandedCard === card.channel"
              @toggle="toggleCard(card.channel)"
              @copy="handleCardCopy"
              @use-content="handleUseContent"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useRouter } from "vue-router";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { Icon } from "@iconify/vue";
import { Button } from "@/components/ui";
import OutreachCueCard from "./OutreachCueCard.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import {
  getCueCards,
  setCueCards,
  updateCueCardsSaved,
  clearCueCards,
  getHistoryCache,
  setHistoryCache,
  invalidateHistoryCache,
  fetchOutreachHistory,
  startBackgroundGeneration,
  registerMountedComponent,
  unregisterMountedComponent,
  isGenerationPending,
} from "@/store/outreachCueCards";
import type {
  OutreachCueCardsData,
  OutreachChannel,
  CueCardConfig,
  OutreachHistoryItem,
} from "@/types/outreach-cue-cards";
defineOptions({
  name: "OutreachCueCards",
});

interface ICPOption {
  value: string;
  label: string;
}

interface Props {
  icpOptions: ICPOption[];
  contactId?: string;
  contactName?: string;
  fullWidth?: boolean;
  icpLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  contactId: "",
  contactName: "",
  fullWidth: false,
  icpLoading: false,
});

const emit = defineEmits<{
  "use-content": [
    payload: {
      channel: string;
      content: string;
      subject?: string;
      callSteps?: { label: string; content: string; color: string }[];
    },
  ];
  "generation-complete": [data: OutreachCueCardsData];
  "generation-error": [error: Error];
}>();

const router = useRouter();
const $toast = useToast();
const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// Callbacks registered with the background generation system so it can notify
// this component when it's still mounted, or fall back to a toast when it isn't.
const generationCallbacks = {
  onSuccess() {
    const cached = getCueCards(props.contactId);
    if (!cached) return;
    currentRecordId.value = cached.recordId;
    isSaved.value = cached.isSaved;
    cueCardsData.value = cached.data;
    state.value = "ready";
    isPanelOpen.value = true;
    expandedCard.value = null;
    emit("generation-complete", cached.data);
  },
  async onError(err: Error) {
    console.error("Failed to generate outreach cue cards:", err);
    try {
      const items = await AIGenerationService.getOutreachHistory(props.contactId);
      if (items.length > 0) {
        const latest = items[0];
        cueCardsData.value = latest.data;
        currentRecordId.value = latest.id;
        isSaved.value = latest.isSaved;
        lastIcpId.value = latest.icpId;
        state.value = "ready";
        isPanelOpen.value = true;
        if (props.contactId && latest.icpId) {
          setCueCards(props.contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
          invalidateHistoryCache(props.contactId);
        }
        return;
      }
    } catch {
      // fall through to error state
    }
    $toast?.error(
      (err as any)?.response?.data?.msg ||
        "Generation timed out. Click 'Check for Results' to retry."
    );
    state.value = "error";
    emit("generation-error", err);
  },
};

// State machine
const state = ref<"initial" | "checking" | "loading" | "ready" | "error">("initial");
const isDropdownOpen = ref(false);
const isPanelOpen = ref(true);
const expandedCard = ref<OutreachChannel | null>(null);
const cueCardsData = ref<OutreachCueCardsData | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const readyRef = ref<HTMLElement | null>(null);
const readyTriggerRef = ref<HTMLElement | null>(null);
const panelPos = ref({ top: 0, left: 0, width: 0 });

// Save / History / Regenerate state
const currentRecordId = ref<string | null>(null);
const lastIcpId = ref<string | null>(null);
const isSaved = ref(false);
const isHistoryOpen = ref(false);
const historyItems = ref<OutreachHistoryItem[]>([]);
const isHistoryLoading = ref(false);

const sectionStyle = computed(() => ({
  width: props.fullWidth ? "100%" : "calc(100% + 24px)",
  marginLeft: props.fullWidth ? "0" : "-12px",
  marginRight: props.fullWidth ? "0" : "-12px",
  marginBottom: "8px",
  paddingLeft: props.fullWidth ? "0" : "12px",
  paddingRight: props.fullWidth ? "0" : "12px",
}));

const panelPositionStyle = computed(() => ({
  position: "fixed" as const,
  top: `${panelPos.value.top}px`,
  left: `${panelPos.value.left}px`,
  width: `${panelPos.value.width}px`,
}));

function updatePanelPosition() {
  const el = readyTriggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  panelPos.value = {
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  };
}

function togglePanel() {
  isPanelOpen.value = !isPanelOpen.value;
  if (isPanelOpen.value) {
    updatePanelPosition();
  }
}

const hasNoICPs = computed(() => !props.icpOptions || props.icpOptions.length <= 1);

const selectableOptions = computed(() => props.icpOptions.filter((opt) => opt.value !== ""));

const cueCardConfigs: CueCardConfig[] = [
  {
    channel: "call",
    title: "Cold Call Script",
    shortTitle: "Cold Call",
    icon: "mdi:phone-outline",
    badgeLabel: "Call",
    badgeVariant: "call",
  },
  {
    channel: "voicemail",
    title: "Voicemail Script",
    shortTitle: "Voicemail",
    icon: "mdi:voicemail",
    badgeLabel: "Voicemail",
    badgeVariant: "call",
  },
  {
    channel: "email",
    title: "Cold Email",
    shortTitle: "Cold Email",
    icon: "mdi:email-outline",
    badgeLabel: "Email",
    badgeVariant: "email",
  },
  {
    channel: "linkedin_connection",
    title: "LinkedIn Connection Note",
    shortTitle: "LinkedIn",
    icon: "mdi:linkedin",
    badgeLabel: "LinkedIn",
    badgeVariant: "linkedin",
  },
  {
    channel: "linkedin_followup",
    title: "LinkedIn Follow-up DM",
    shortTitle: "LinkedIn",
    icon: "mdi:linkedin",
    badgeLabel: "LinkedIn",
    badgeVariant: "linkedin",
  },
  {
    channel: "linkedin_inmail",
    title: "LinkedIn InMail",
    shortTitle: "InMail",
    icon: "mdi:email-fast-outline",
    badgeLabel: "InMail",
    badgeVariant: "linkedin",
  },
  {
    channel: "whatsapp",
    title: "WhatsApp Message",
    shortTitle: "WhatsApp",
    icon: "mdi:whatsapp",
    badgeLabel: "WhatsApp",
    badgeVariant: "whatsapp",
  },
  {
    channel: "sms",
    title: "SMS Message",
    shortTitle: "SMS",
    icon: "mdi:message-text-outline",
    badgeLabel: "SMS",
    badgeVariant: "sms",
  },
];

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function toggleCard(channel: OutreachChannel) {
  expandedCard.value = expandedCard.value === channel ? null : channel;
}

async function generate(icpId: string, force = false) {
  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    state.value = "loading";
    isDropdownOpen.value = false;
    lastIcpId.value = icpId;
    isHistoryOpen.value = false;

    startBackgroundGeneration(
      props.contactId,
      icpId,
      props.contactName,
      String(router.currentRoute.value.name ?? ""),
      { ...router.currentRoute.value.query },
      force || undefined
    );
  });
}

function handleICPSelect(icpId: string) {
  generate(icpId);
}

async function handleRegenerate() {
  if (!lastIcpId.value) {
    $toast?.warning("Please select an ICP first");
    return;
  }
  await generate(lastIcpId.value, true);
}

async function retryFromHistory() {
  if (!props.contactId) return;
  state.value = "loading";
  try {
    const items = await AIGenerationService.getOutreachHistory(props.contactId);
    if (items.length > 0) {
      const latest = items[0];
      cueCardsData.value = latest.data;
      currentRecordId.value = latest.id;
      isSaved.value = latest.isSaved;
      lastIcpId.value = latest.icpId;
      state.value = "ready";
      isPanelOpen.value = true;
      if (latest.icpId) {
        setCueCards(props.contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
        invalidateHistoryCache(props.contactId);
      }
    } else {
      $toast?.warning("Results not ready yet. Please try again in a moment.");
      state.value = "error";
    }
  } catch {
    $toast?.error("Failed to check for results.");
    state.value = "error";
  }
}

function handleReset() {
  if (props.contactId) {
    clearCueCards(props.contactId);
  }
  state.value = "initial";
  isPanelOpen.value = true;
  isDropdownOpen.value = false;
  isHistoryOpen.value = false;
  cueCardsData.value = null;
  currentRecordId.value = null;
  lastIcpId.value = null;
  isSaved.value = false;
  expandedCard.value = null;
}

async function handleSave() {
  if (!currentRecordId.value) {
    $toast?.warning("No content to save");
    return;
  }

  try {
    const result = await AIGenerationService.toggleSaveOutreach(currentRecordId.value);
    isSaved.value = result.isSaved;
    if (props.contactId) {
      updateCueCardsSaved(props.contactId, result.isSaved);
    }
    $toast?.success(result.isSaved ? "Saved" : "Removed from saved");
  } catch (err: any) {
    console.error("Failed to toggle save:", err);
    $toast?.error("Failed to save content");
  }
}

async function handleHistory() {
  if (isHistoryOpen.value) {
    isHistoryOpen.value = false;
    return;
  }

  isHistoryOpen.value = true;

  const cached = props.contactId ? getHistoryCache(props.contactId) : null;
  if (cached) {
    historyItems.value = cached;
    return;
  }

  isHistoryLoading.value = true;

  try {
    const items = await AIGenerationService.getOutreachHistory(props.contactId);
    historyItems.value = items;
    if (props.contactId) {
      setHistoryCache(props.contactId, items);
    }
  } catch (err: any) {
    console.error("Failed to load history:", err);
    $toast?.error("Failed to load history");
    historyItems.value = [];
  } finally {
    isHistoryLoading.value = false;
  }
}

function loadHistoryItem(item: OutreachHistoryItem) {
  cueCardsData.value = item.data;
  currentRecordId.value = item.id;
  isSaved.value = item.isSaved;
  expandedCard.value = null;
  if (props.contactId && item.icpId) {
    setCueCards(props.contactId, item.data, item.id, item.icpId, item.isSaved);
  }
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getHistoryPreview(item: OutreachHistoryItem): string {
  const subject = item.data?.email?.subject;
  if (subject) {
    return subject.length > 50 ? subject.slice(0, 50) + "..." : subject;
  }
  return "Outreach content";
}

function handleCardCopy(_channel: string) {
  $toast?.success("Copied to clipboard");
}

function handleUseContent(payload: {
  channel: string;
  content: string;
  subject?: string;
  callSteps?: { label: string; content: string; color: string }[];
}) {
  const plainTextChannels = [
    "whatsapp",
    "sms",
    "linkedin_connection",
    "linkedin_followup",
    "linkedin_inmail",
  ];
  if (plainTextChannels.includes(payload.channel)) {
    payload.content = payload.content
      .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, "\n\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  if (payload.callSteps) {
    payload.callSteps = payload.callSteps.map((step) => ({
      ...step,
      content: step.content.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, ""),
    }));
  }

  isPanelOpen.value = false;
  expandedCard.value = null;
  emit("use-content", payload);
}

function goToICPSettings() {
  router.push({ path: "/settings/general", query: { tab: "icp" } });
}

function goToEditICP(icpId: string) {
  router.push({ path: "/settings/general", query: { tab: "icp", icpId } });
}

function goToAddICP() {
  router.push({ path: "/settings/general", query: { tab: "icp" } });
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
}

async function initFromCacheOrHistory(contactId: string) {
  if (!contactId || hasNoICPs.value) return;

  // 1. Check in-memory cache
  const cached = getCueCards(contactId);
  if (cached) {
    cueCardsData.value = cached.data;
    currentRecordId.value = cached.recordId;
    lastIcpId.value = cached.icpId;
    isSaved.value = cached.isSaved;
    state.value = "ready";
    isPanelOpen.value = false;
    return;
  }

  // 2. Fetch from API (deduped across instances)
  state.value = "checking";
  try {
    const items = await fetchOutreachHistory(contactId);
    // Guard: bail if contactId changed or user already took action
    if (props.contactId !== contactId || state.value !== "checking") return;
    if (items.length > 0) {
      const latest = items[0];
      cueCardsData.value = latest.data;
      currentRecordId.value = latest.id;
      isSaved.value = latest.isSaved;
      lastIcpId.value = latest.icpId;
      state.value = "ready";
      isPanelOpen.value = false;
      if (latest.icpId) {
        setCueCards(contactId, latest.data, latest.id, latest.icpId, latest.isSaved);
      }
    } else {
      state.value = "initial";
    }
  } catch (err) {
    console.warn("[OutreachCueCards] Failed to load history on init:", err);
    // Fall back to "initial" — cache watcher will pick up data if another panel succeeds
    if (props.contactId === contactId && state.value === "checking") {
      state.value = "initial";
    }
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  registerMountedComponent(props.contactId, generationCallbacks);
  if (isGenerationPending(props.contactId)) {
    state.value = "loading";
  } else {
    initFromCacheOrHistory(props.contactId);
  }
});

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Reset for new contact
      state.value = "initial";
      cueCardsData.value = null;
      currentRecordId.value = null;
      lastIcpId.value = null;
      isSaved.value = false;
      expandedCard.value = null;
      isDropdownOpen.value = false;
      isHistoryOpen.value = false;
      historyItems.value = [];
      initFromCacheOrHistory(newId);
    }
  }
);

watch(
  () => getCueCards(props.contactId),
  (cached) => {
    if (!cached) return;
    const wasLoading = state.value === "loading";
    const isStale =
      wasLoading ||
      state.value === "initial" ||
      state.value === "checking" ||
      state.value === "error";
    const isNewRecord = state.value === "ready" && cached.recordId !== currentRecordId.value;
    if (isStale || isNewRecord) {
      cueCardsData.value = cached.data;
      currentRecordId.value = cached.recordId;
      lastIcpId.value = cached.icpId;
      isSaved.value = cached.isSaved;
      state.value = "ready";
      if (wasLoading) {
        isPanelOpen.value = true;
        expandedCard.value = null;
        emit("generation-complete", cached.data);
      } else if (!isNewRecord) {
        isPanelOpen.value = false;
      }
    }
  }
);

watch(hasNoICPs, (noICPs, wasNoICPs) => {
  if (!noICPs && wasNoICPs && props.contactId && state.value === "initial") {
    initFromCacheOrHistory(props.contactId);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  unregisterMountedComponent(props.contactId);
});
</script>

<style scoped>
.outreach-cue-cards {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-shrink: 0;
}

.outreach-cue-cards__empty-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(62, 62, 254, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%);
  border: 1px dashed rgba(62, 62, 254, 0.3);
  border-radius: 8px;
}

.outreach-cue-cards__empty-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.outreach-cue-cards__empty-icon {
  font-size: 1.125rem;
  color: #3e3efe;
  flex-shrink: 0;
}

.outreach-cue-cards__empty-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #4b5563;
  line-height: 1.3;
  white-space: nowrap;
}

.outreach-cue-cards__empty-btn {
  flex-shrink: 0;
}

.outreach-cue-cards__trigger-wrapper,
.outreach-cue-cards__ready-wrapper {
  position: relative;
}

.outreach-cue-cards__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.outreach-cue-cards__trigger--initial {
  background: #1e1b4b;
  color: #ffffff;
}

.outreach-cue-cards__trigger--initial:hover {
  background: #312e81;
}

.outreach-cue-cards__trigger--loading {
  background: #8b7cf6;
  color: #ffffff;
  opacity: 0.85;
  cursor: not-allowed;
}

.outreach-cue-cards__trigger--ready {
  background: #059669;
  color: #ffffff;
}

.outreach-cue-cards__trigger--ready:hover {
  background: #047857;
}

.outreach-cue-cards__trigger-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.outreach-cue-cards__trigger-chevron {
  font-size: 1.125rem;
  margin-left: auto;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.outreach-cue-cards__trigger-chevron--open {
  transform: rotate(180deg);
}

.outreach-cue-cards__spinner {
  font-size: 1.125rem;
  flex-shrink: 0;
  animation: outreach-spin 1s linear infinite;
}

@keyframes outreach-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.outreach-cue-cards__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}

.outreach-cue-cards__dropdown-header {
  padding: 8px 14px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #f3f4f6;
}

.outreach-cue-cards__dropdown-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
}

.outreach-cue-cards__dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #111827;
  transition: background 0.15s;
  text-align: left;
}

.outreach-cue-cards__dropdown-option:hover {
  background: #f3f4f6;
}

.outreach-cue-cards__dropdown-option-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.outreach-cue-cards__dropdown-option-edit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 0.875rem;
  color: #9ca3af;
  border-radius: 4px;
  flex-shrink: 0;
  transition: all 0.15s;
}

.outreach-cue-cards__dropdown-option-edit:hover {
  color: #4b5563;
  background: #e5e7eb;
}

.outreach-cue-cards__dropdown-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0;
}

.outreach-cue-cards__dropdown-add {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.15s;
}

.outreach-cue-cards__dropdown-add:hover {
  background: #f9fafb;
  color: #3e3efe;
}

.outreach-cue-cards__dropdown-add-icon {
  font-size: 1rem;
}

.outreach-cue-cards__panel {
  z-index: 1050;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  max-height: 60vh;
  overflow-y: auto;
}

.outreach-cue-cards__toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.outreach-cue-cards__toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.outreach-cue-cards__toolbar-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.outreach-cue-cards__toolbar-btn--reset {
  margin-left: auto;
}

.outreach-cue-cards__toolbar-btn--reset:hover {
  color: #dc2626;
  background: #fef2f2;
}

.outreach-cue-cards__history {
  border-bottom: 1px solid #f3f4f6;
  max-height: 240px;
  overflow-y: auto;
}

.outreach-cue-cards__history-loading,
.outreach-cue-cards__history-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  font-size: 0.75rem;
  color: #9ca3af;
}

.outreach-cue-cards__history-list {
  display: flex;
  flex-direction: column;
}

.outreach-cue-cards__history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f9fafb;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.outreach-cue-cards__history-item:hover {
  background: #f9fafb;
}

.outreach-cue-cards__history-item--active {
  background: #eff6ff;
}

.outreach-cue-cards__history-item--active:hover {
  background: #dbeafe;
}

.outreach-cue-cards__history-item-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.outreach-cue-cards__history-item-time {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
}

.outreach-cue-cards__history-item-preview {
  font-size: 0.75rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.outreach-cue-cards__history-item-saved {
  font-size: 0.875rem;
  color: #3b82f6;
  flex-shrink: 0;
}

.outreach-cue-cards__list {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 8px;
}

.outreach-cue-cards__list > * + * {
  margin-top: 6px;
}

.outreach-cue-cards__error {
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
}

.outreach-cue-cards__error-content {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.outreach-cue-cards__error-icon {
  font-size: 0.875rem;
  color: #d97706;
  flex-shrink: 0;
}

.outreach-cue-cards__error-text {
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.3;
}

.outreach-cue-cards__error-actions {
  display: flex;
  gap: 6px;
}

.outreach-cue-cards__error-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s;
}

.outreach-cue-cards__error-btn--retry {
  background: #3e3efe;
  color: #ffffff;
}

.outreach-cue-cards__error-btn--retry:hover {
  background: #3230d6;
}

.outreach-cue-cards__error-btn--regen {
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.outreach-cue-cards__error-btn--regen:hover {
  background: #f3f4f6;
  color: #374151;
}

.outreach-cue-cards__trigger:disabled,
.outreach-cue-cards__toolbar-btn:disabled,
.outreach-cue-cards__error-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}

.outreach-cue-cards__trigger:disabled:hover,
.outreach-cue-cards__toolbar-btn:disabled:hover,
.outreach-cue-cards__error-btn:disabled:hover {
  background: inherit;
  color: inherit;
}
</style>
